import { useTranslation } from 'react-i18next';

export default function TechAdvantage() {
  const { t } = useTranslation();

  const compareDims = [
    { dim: t('techAdvantage.dim_decision'), human: t('techAdvantage.human_decision'), ai: t('techAdvantage.ai_decision'), icon: 'ri-brain-line' },
    { dim: t('techAdvantage.dim_monitor'), human: t('techAdvantage.human_monitor'), ai: t('techAdvantage.ai_monitor'), icon: 'ri-time-line' },
    { dim: t('techAdvantage.dim_execute'), human: t('techAdvantage.human_execute'), ai: t('techAdvantage.ai_execute'), icon: 'ri-robot-line' },
    { dim: t('techAdvantage.dim_model'), human: t('techAdvantage.human_model'), ai: t('techAdvantage.ai_model'), icon: 'ri-calculator-line' },
    { dim: t('techAdvantage.dim_risk'), human: t('techAdvantage.human_risk'), ai: t('techAdvantage.ai_risk'), icon: 'ri-shield-check-line' },
  ];

  const advantages = [
    { icon: 'ri-database-2-line', titleKey: 'techAdvantage.adv1_title', descKey: 'techAdvantage.adv1_desc', stat: '50+', labelKey: 'techAdvantage.adv1_label' },
    { icon: 'ri-refresh-line', titleKey: 'techAdvantage.adv2_title', descKey: 'techAdvantage.adv2_desc', stat: '24/7', labelKey: 'techAdvantage.adv2_label' },
    { icon: 'ri-flashlight-line', titleKey: 'techAdvantage.adv3_title', descKey: 'techAdvantage.adv3_desc', stat: '<200ms', labelKey: 'techAdvantage.adv3_label' },
    { icon: 'ri-shield-star-line', titleKey: 'techAdvantage.adv4_title', descKey: 'techAdvantage.adv4_desc', stat: '<5%', labelKey: 'techAdvantage.adv4_label' },
  ];

  return (
    <section id="advantage" className="py-16 md:py-24 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #100b1e 0%, #0c0814 100%)' }}>
      <div className="absolute inset-0 grid-bg pointer-events-none opacity-50" />

      <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
        <div className="text-center mb-10 md:mb-16">
          <div className="badge-purple mb-6">{t('techAdvantage.badge')}</div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-5 font-heading">
            {t('techAdvantage.title1')}<br />
            <span className="text-gradient-aurora">{t('techAdvantage.title2')}</span>
          </h2>
        </div>

        {/* Comparison Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5 mb-12 md:mb-16">
          {compareDims.map((row, index) => (
            <div key={index} className="premium-card premium-card-hover rounded-2xl p-4 md:p-5">
              <div className="flex items-center justify-center mb-3 md:mb-4">
                <div className="w-10 md:w-12 h-10 md:h-12 rounded-xl bg-purple-400/10 border border-purple-400/20 flex items-center justify-center">
                  <i className={`${row.icon} text-xl md:text-2xl text-purple-400`}></i>
                </div>
              </div>
              <h3 className="text-sm md:text-base font-bold text-white text-center mb-3 md:mb-4">{row.dim}</h3>
              <div className="space-y-2 md:space-y-3">
                <div className="bg-white/[0.03] rounded-lg p-2 md:p-3 text-center border border-white/5">
                  <div className="text-xs text-white/30 mb-1">{t('techAdvantage.humanPredict')}</div>
                  <div className="text-xs md:text-sm font-medium text-white/50">{row.human}</div>
                </div>
                <div className="rounded-lg p-2 md:p-3 text-center border border-purple-400/18"
                  style={{ background: 'rgba(147,51,234,0.08)' }}>
                  <div className="text-xs text-purple-400 mb-1">PolyAi-Claw</div>
                  <div className="text-xs md:text-sm font-bold text-purple-300 flex items-center justify-center gap-1">
                    {row.ai}
                    <i className="ri-checkbox-circle-fill text-purple-400 text-xs"></i>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Key Advantages */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 mb-10 md:mb-14">
          {advantages.map(item => (
            <div key={item.titleKey} className="premium-card premium-card-hover rounded-2xl p-6 md:p-8">
              <div className="flex items-start gap-4 mb-5">
                <div className="w-12 md:w-14 h-12 md:h-14 rounded-xl bg-purple-400/10 border border-purple-400/20 flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} text-2xl text-purple-400`}></i>
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-bold text-white mb-2 font-heading">{t(item.titleKey)}</h3>
                  <p className="text-white/50 leading-relaxed text-sm md:text-base">{t(item.descKey)}</p>
                </div>
              </div>
              <div className="bg-white/[0.03] border border-purple-500/8 rounded-xl p-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-white/35">{t(item.labelKey)}</span>
                  <span className="text-xl md:text-2xl font-bold text-purple-400">{item.stat}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Conclusion */}
        <div className="rounded-2xl p-7 md:p-10 text-center border border-purple-400/18"
          style={{ background: 'linear-gradient(135deg, rgba(147,51,234,0.1), rgba(124,58,237,0.06))' }}>
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-4 font-heading">{t('techAdvantage.conclusion_title')}</h3>
          <p className="text-base md:text-lg text-white/50 leading-relaxed max-w-4xl mx-auto">
            {t('techAdvantage.conclusion_desc1')}<br />
            <strong className="text-purple-300">{t('techAdvantage.conclusion_brand')}</strong><br />
            {t('techAdvantage.conclusion_desc2')}
          </p>
        </div>
      </div>
    </section>
  );
}
